import { EyeIcon, EyeSlashIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button } from 'stories/components'
import { generateUniqeId, InputConvert, InputValidate, prompt } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { EmailDNSConfig } from '../../EmailDNSSetup/type'
import { defaultInputs } from './constants'
import { IEmailFrom } from './type'

export const EmailSetup = () => {
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [emailDns, setEmailDns] = useState<Record<string, string>>({})
  const [selectedEmailDns, setSelectedEmailDns] = useState<string>('')
  const [edit, setEdit] = useState(false)
  const [action, setAction] = useState('')
  const [fromEmails, setFromEmails] = useState<IEmailFrom[]>()
  const [isLoading, setIsLoading] = useState(true)
  const [emailDnsConfig, setEmailDnsConfig] = useState<EmailDNSConfig[]>([])

  useEffect(() => {
    Promise.all([getAdminConfig('emailSetup'), getAdminConfig('emailDNSSetup')])
      .then(([emailSetup, dnsSetup]) => {
        setAction('setting')
        setFromEmails(emailSetup.emails)

        let emailInputs: Record<string, InputType> = {}
        let emailDns: Record<string, string> = {}
        emailSetup.emails.forEach((emailFrom: IEmailFrom) => {
          const { key, value, title, dns, isActive } = emailFrom
          emailInputs[key] = {
            inputType: 'text',
            title: title || key,
            value: value,
            required: true,
            visible: isActive === undefined ? true : isActive,
          }
          emailDns[key] = dns || ''
        })

        const newInputs = cloneDeep(defaultInputs())
        newInputs.adminEmailDns.value = emailSetup.adminEmailDns
        newInputs.defaultReceiver.value = emailSetup.defaultReceiver

        setInputs({
          ...emailInputs,
          ...newInputs,
        })
        setEmailDnsConfig(dnsSetup)
        setEmailDns(emailDns)
        setIsLoading(false)
      })
      .finally(() => setAction(''))

    setEdit(false)
  }, [])

  const primaryDns = useMemo(() => {
    const primary = emailDnsConfig.find((v) => v.isPrimary)
    if (!primary) return ''
    return primary.dns
  }, [emailDnsConfig])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
    setEdit(true)
  }

  const onAdd = async () => {
    const newTitle = await prompt('Enter email title.')
    if (!newTitle) return

    const defaultInputKeys = Object.keys(defaultInputs())
    let emailInputs: Record<string, InputType> = {}

    Object.keys(inputs).forEach((key) => {
      if (defaultInputKeys.includes(key)) return
      emailInputs[key] = inputs[key]
    })

    const newKey = generateUniqeId(newTitle, Object.keys(inputs))
    emailInputs[newKey] = {
      inputType: 'text',
      title: newTitle,
      value: newKey,
      required: true,
      visible: true,
    }
    defaultInputKeys.forEach((key) => {
      emailInputs[key] = inputs[key]
    })

    setInputs(emailInputs)
    onChangeEmailDns(newKey, primaryDns)
  }

  const onSubmit = async () => {
    if (!fromEmails) return
    let hasError = false

    const newInputs = cloneDeep(inputs)
    const emails: IEmailFrom[] = []
    const data: Record<string, any> = {}
    const defaultInputKeys = Object.keys(defaultInputs())

    for (const key in inputs) {
      if (key == 'divider') continue

      newInputs[key].error = InputValidate(newInputs[key])

      if (defaultInputKeys.includes(key)) data[key] = newInputs[key].value
      else
        emails.push({
          key,
          value: newInputs[key].value,
          dns: emailDns[key],
          title: newInputs[key].title,
          isActive: !!newInputs[key].visible,
        })
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    if (hasError) return

    setAction('setting')
    await setAdminConfig('emailSetup', {
      emails,
      ...data,
    })
    setAction('')
    setEdit(false)
  }

  const onChangeEmailDns = (key: string, value: string) => {
    const newValues = cloneDeep(emailDns)
    newValues[key] = value
    setEmailDns(newValues)
    setEdit(true)
  }

  const onChangeSelectedEmailDns = (value: string) => {
    setSelectedEmailDns(value)
    const newValues = cloneDeep(emailDns)
    Object.keys(newValues).forEach((key) => {
      newValues[key] = value
    })
    setEmailDns(newValues)
    setEdit(true)
  }

  const renderEmailDnsOptions = (key: string) => {
    const onChangeEmailTitle = async (key: string) => {
      const newTitle = await prompt('Enter email title.', { value: inputs[key].title })
      if (!newTitle) return
      const newInputs = cloneDeep(inputs)
      newInputs[key].title = newTitle
      setInputs(newInputs)
      setEdit(true)
    }

    const onChangeVisible = (key: string) => {
      const newInputs = cloneDeep(inputs)
      newInputs[key].visible = !newInputs[key].visible
      setInputs(newInputs)
      setEdit(true)
    }

    const VisibleIcon = inputs[key].visible ? EyeIcon : EyeSlashIcon
    return (
      <div className="flex items-center gap-2">
        <span className="btn-icon" onClick={() => onChangeEmailTitle(key)}>
          <PencilSquareIcon className="w-4 h-4 text-gray-500" />
        </span>
        <span className="btn-icon" onClick={() => onChangeVisible(key)}>
          <VisibleIcon className="w-4 h-4 text-gray-500" />
        </span>
        <select
          onChange={(e) => onChangeEmailDns(key, e.target.value)}
          value={emailDns[key] || primaryDns}
          className="block rounded py-0 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
        >
          {emailDnsConfig
            .filter((v) => v.verified)
            .map((item) => (
              <option key={item.dns}>{item.dns}</option>
            ))}
        </select>
      </div>
    )
  }

  const renderSelectEmailDns = () => {
    return (
      <div className="flex items-center gap-2">
        <select
          onChange={(e) => onChangeSelectedEmailDns(e.target.value)}
          value={selectedEmailDns}
          className="block rounded py-0 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
        >
          <option value="" disabled>
            Select Email DNS
          </option>
          {emailDnsConfig
            .filter((v) => v.verified)
            .map((item) => (
              <option key={item.dns}>{item.dns}</option>
            ))}
        </select>
      </div>
    )
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <div className="flex gap-2 items-center mb-4">
        <div className="font-semibold text-xl"> Primary DNS : @{primaryDns}</div>
        {renderSelectEmailDns()}
      </div>
      <div className="relative mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.keys(inputs).map((key, index) => {
          let input = inputs[key]
          if (input.inputType === 'custom') return <div className="col-span-full border-b"></div>
          if (!['adminEmailDns', 'defaultReceiver'].includes(key)) input.additionalElements = renderEmailDnsOptions(key)

          return (
            <div className={`input md:col-span-${input.span || 1} ${input.visible ? '' : 'opacity-30'}`} key={index}>
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
        <p className="col-span-full italic">
          Administrator Emails will be filtered by this. Use semicolon(;) to separate multiple domains
        </p>
      </div>
      <div className="flex justify-center">
        <Button outline className="w-[200px]" loading={!!action} onClick={onAdd}>
          Add new email
        </Button>
        {edit && (
          <Button className="w-[200px]" loading={action == 'setting'} onClick={onSubmit}>
            Save
          </Button>
        )}
      </div>
    </div>
  )
}
